import gql from "graphql-tag";

import {isTruthy} from "@khanacademy/wonder-stuff-core";
import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import {getTextForBadge} from "#badges-package/badge-translations.ts";

import type {
    getCombinedBadges_allBadges as AllBadgeData,
    getCombinedBadges as GetCombinedBadges,
    getCombinedBadgesVariables as GetCombinedBadgesVariables,
    getCombinedBadges_user_badges as UserBadgeData,
} from "./__graphql-types__/getCombinedBadges.ts";
import type {
    getPublicBadgesForProfiles as GetPublicBadges,
    getPublicBadgesForProfiles_user_profile_badges as PublicBadge,
    getPublicBadgesForProfilesVariables as PublicBadgesVariables,
} from "./__graphql-types__/getPublicBadgesForProfiles.ts";

export type Badge = {
    badgeCategory: number;
    description: string;
    fullDescription: string;
    isOwned: boolean;
    isCustom: boolean;
    isRetired: boolean;
    name: string;
    points: number;
    absoluteUrl: string;
    hideContext: boolean;
    icons: {
        small: string;
        compact: string;
        email: string;
        large: string;
    };
    relativeUrl: string;
    slug: string;
    translatedDescription: string;
    translatedExtendedDescription: string;
    iconSrc: string;
};

export type UserBadge = {
    lastEarnedDate: string;
    count: number;
    badge: Badge;
};

type Badges = Array<Badge>;
type UserBadges = Array<UserBadge>;

const BADGE_FRAGMENT = gql`
    fragment Badge on Badge {
        badgeCategory
        description
        fullDescription
        isCustom
        isRetired
        name
        points
        absoluteUrl
        hideContext
        icons {
            smallUrl
            compactUrl
            emailUrl
            largeUrl
        }
        relativeUrl
        slug
    }
`;

export const GET_PUBLIC_BADGES_QUERY: GqlOperation<
    GetPublicBadges,
    PublicBadgesVariables
> = gqlOp(gql`
    query getPublicBadgesForProfiles($kaid: String) {
        user(kaid: $kaid) {
            id
            profile {
                badges {
                    ...Badge
                }
            }
        }
    }

    ${BADGE_FRAGMENT}
`);

export const GET_COMBINED_BADGES_QUERY: GqlOperation<
    GetCombinedBadges,
    GetCombinedBadgesVariables
> = gqlOp(gql`
    query getCombinedBadges($kaid: String, $spotlightBadgeSlug: String!) {
        user(kaid: $kaid) {
            id
            badges {
                lastEarnedDate
                count
                badge {
                    ...Badge
                }
            }
        }
        allBadges(excludeFilters: [RETIRED, CUSTOM, HIDDEN_IF_UNKNOWN]) {
            ...Badge
        }
        badgeBySlug(slug: $spotlightBadgeSlug) {
            ...Badge
        }
    }

    ${BADGE_FRAGMENT}
`);

export function mapBadge(badge: AllBadgeData, isOwned: boolean): Badge {
    const translation = getTextForBadge(badge, isOwned);
    const {__typename: _, ...rest} = badge;
    return {
        ...rest,
        translatedDescription: translation.translatedDescription,
        translatedExtendedDescription: translation.translatedFullDescription,
        iconSrc: badge.icons.smallUrl,
        icons: {
            small: badge.icons.smallUrl,
            compact: badge.icons.compactUrl,
            email: badge.icons.emailUrl,
            large: badge.icons.largeUrl,
        },
        isOwned,
    };
}

// Although this is mapping over allBadges, we requires userBadges as well
// because the UI needs to dispaly ownership information in the list of
// allBadges. Thus we need to know which badges are owned to map the allBadges
// list to a list of UserBadges.
function mapBadges(
    allBadges: Array<AllBadgeData | null | undefined>,
    userBadges: Array<UserBadgeData | null | undefined>,
): UserBadges {
    const ownedBadgesMap = new Map();
    userBadges.forEach((userBadge) => {
        if (userBadge) {
            ownedBadgesMap.set(userBadge.badge.name, userBadge);
        }
    });

    return allBadges.filter(isTruthy).map((badge) => {
        const ownedBadge = ownedBadgesMap.get(badge.name);
        return {
            lastEarnedDate: ownedBadge ? ownedBadge.lastEarnedDate : "",
            count: ownedBadge ? ownedBadge.count : 0,
            badge: mapBadge(badge, !!ownedBadge),
        };
    });
}

// Like the above mapBadges but with no concept of ownership or UserBadges.
function mapPublicBadges(
    publicBadges: Array<PublicBadge | null | undefined>,
): Badges {
    // Presumably all public badges for a user are owned but passing false for
    // isOwned matches existing behaviour.
    return publicBadges.filter(isTruthy).map((badge) => mapBadge(badge, false));
}

// Callers should set areOwnedByUser true IFF all userBadges have been earned.
// There should not be a mix of earned/unearned userBadges being passed to this
// function.
function mapUserBadges(
    userBadges: Array<UserBadgeData | null | undefined>,
    areOwnedByUser: boolean,
): UserBadges {
    return userBadges.filter(isTruthy).map((userBadge: UserBadgeData) => ({
        lastEarnedDate: userBadge.lastEarnedDate,
        count: userBadge.count,
        badge: mapBadge(userBadge.badge, areOwnedByUser),
    }));
}

export function getPublicBadges(
    gqlClient: ApolloGqlClient,
    kaid?: string,
): Promise<Badges> {
    return gqlClient
        .query(GET_PUBLIC_BADGES_QUERY, {
            fetchPolicy: "network-only",
            variables: {kaid},
        })
        .then((result) =>
            mapPublicBadges(result.data?.user?.profile?.badges || []),
        );
}

export type CombinedBadges = {
    allBadges: UserBadges;
    userBadges: UserBadges;
    spotlightBadge?: UserBadge;
};

export function getCombinedBadges(
    gqlClient: ApolloGqlClient,
    kaid?: string | null,
    spotlightBadgeSlug?: string | null,
): Promise<CombinedBadges> {
    const spotlightSlug = spotlightBadgeSlug || "";
    return gqlClient
        .query(GET_COMBINED_BADGES_QUERY, {
            fetchPolicy: "network-only",
            variables: {kaid, spotlightBadgeSlug: spotlightSlug},
        })
        .then((result) => {
            const allBadgesData = result.data?.allBadges || [];
            const userBadgesData = result.data?.user?.badges || [];

            const spotlightBadgeData = [result.data?.badgeBySlug];
            const spotlightUserBadgeArray = mapBadges(
                spotlightBadgeData,
                userBadgesData,
            );

            return {
                allBadges: mapBadges(allBadgesData, userBadgesData),
                userBadges: mapUserBadges(userBadgesData, true),
                ...(spotlightUserBadgeArray.length > 0
                    ? {spotlightBadge: spotlightUserBadgeArray[0]}
                    : null),
            };
        });
}
